import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "@/components/common/Wordmark";
import { Ridgeline } from "@/components/common/Ridgeline";
import { useNavTheme } from "@/hooks/useNavTheme";
import { usePrefersReducedMotion } from "@/hooks/useReducedMotion";
import { useAudienceMode } from "@/hooks/useAudienceMode";

const LINKS = [
  { label: "Rentals", to: "/rentals" },
  { label: "Relocating", to: "/relocating" },
  { label: "Owners", to: "/owners" },
  { label: "Neighbourhoods", to: "/neighbourhoods" },
  { label: "About", to: "/about" },
];

// CTA copy + destination shifts based on who the current page is written for.
const CTAS = {
  renter: { label: "Book a showing", to: "/contact" },
  owner: { label: "I own a property", to: "/owners" },
};

export const Navbar = () => {
  const theme = useNavTheme(72);
  const dark = theme === "dark";
  const reduced = usePrefersReducedMotion();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const audience = useAudienceMode();
  const cta = CTAS[audience];

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const textColor = dark ? "#EDE9E0" : "#24382E";
  const wordmarkColor = dark ? "#EDE9E0" : "#24382E";

  return (
    <>
      <header
        className="fixed inset-x-0 top-0 z-50"
        data-testid="navbar"
        style={{
          backgroundColor: dark ? "rgba(20,35,27,0.82)" : "rgba(237,233,224,0.86)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderBottom: `1px solid ${dark ? "rgba(200,154,78,0.18)" : "rgba(32,30,26,0.08)"}`,
          transition: "background-color 0.5s ease, border-color 0.5s ease",
        }}
      >
        <nav className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-6 md:px-10">
          <Wordmark
            color={wordmarkColor}
            goldColor="#C89A4E"
            size="text-xl md:text-2xl"
            testId="nav-wordmark"
          />

          <div className="hidden items-center gap-9 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="label-caps relative text-[13px]"
                style={({ isActive }) => ({
                  color: textColor,
                  opacity: isActive ? 1 : 0.72,
                })}
              >
                {({ isActive }) => (
                  <span className="group relative inline-block py-1">
                    {l.label}
                    <span
                      className="absolute -bottom-0.5 left-0 h-px w-full origin-left transition-transform duration-500 ease-out group-hover:scale-x-100"
                      style={{
                        backgroundColor: "#C89A4E",
                        transform: isActive ? "scaleX(1)" : "scaleX(0)",
                      }}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Link
              to={cta.to}
              data-testid="nav-cta"
              className={`pill hidden text-sm sm:inline-flex ${dark ? "pill-gold" : "pill-pine"}`}
              style={{ padding: "0.7rem 1.5rem" }}
            >
              {cta.label}
            </Link>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Open menu"
              className="lg:hidden"
              style={{ color: textColor }}
            >
              <Menu size={26} strokeWidth={1.5} />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="grain fixed inset-0 z-[60] flex flex-col lg:hidden"
            style={{ backgroundColor: "#24382E" }}
            initial={reduced ? { opacity: 0 } : { clipPath: "circle(0% at 90% 5%)" }}
            animate={reduced ? { opacity: 1 } : { clipPath: "circle(150% at 90% 5%)" }}
            exit={reduced ? { opacity: 0 } : { clipPath: "circle(0% at 90% 5%)" }}
            transition={{ duration: reduced ? 0.3 : 0.7, ease: [0.16, 1, 0.3, 1] }}
            data-testid="mobile-menu"
          >
            <div className="flex h-[72px] items-center justify-between px-6">
              <Wordmark color="#EDE9E0" goldColor="#E3B268" size="text-xl" asLink={false} />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                style={{ color: "#EDE9E0" }}
              >
                <X size={28} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: reduced ? 0 : 0.15 + i * 0.08,
                    duration: 0.6,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-link-${l.label.toLowerCase()}`}
                    className="font-display block leading-none"
                    style={{ color: "#F8F5EF", fontSize: "clamp(40px, 12vw, 68px)" }}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: reduced ? 0 : 0.15 + LINKS.length * 0.08, duration: 0.6 }}
                className="mt-8"
              >
                <Link
                  to={cta.to}
                  data-testid="mobile-cta"
                  className="pill pill-gold text-base"
                >
                  {cta.label}
                </Link>
              </motion.div>
            </div>

            <div className="relative w-full opacity-90">
              <Ridgeline stroke="#C89A4E" height={120} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
